import { useFormContext } from 'react-hook-form';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { array, number, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FormRadioGroupField, lt, useDialog, useFeatureFlag, useLocalizeProperty } from '@mews/framework';
import { TextKey, DataTestId, FeatureFlag } from '@mews-commander/enums';
import { Container, RadioButton } from '@optimus-web/core';
import { Product, Service } from '@mews-commander/data-access';
import { ProductsFormFields, ProductsOptionsNames } from '../../../../../schemas';
import { createProductGroupOptions } from '../../../../../utils';
import { RoutingRulesFormFields } from '../../../utils';
import { useDeletedItems } from '../../../hooks';
import { useProductCategoriesSuspenseQuery, useProductsSuspenseQuery } from '../queries';
import { RadioButtonLabelWithCounter } from '../Conditions/RadioButtonLabelWithCounter';
import { ProductsDialog } from './ProductsDialog';
import { ProductCategoriesDialog } from './ProductCategoriesDialog';
import { useAccountingCategoriesOption } from './hooks';

const productOrderingContramap = pipe(
    number.Ord,
    ord.contramap((product: Product) => product.ordering)
);

export const ProductsSelectField = ({
    service,
    isBookableService,
}: {
    service: Service;
    isBookableService: boolean;
}) => {
    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);
    const [productsDialogProps, openProductsDialog] = useDialog();
    const [productCategoriesDialogProps, openProductCategoriesDialog] = useDialog();
    const { data: products } = useProductsSuspenseQuery();
    const { data: productCategories } = useProductCategoriesSuspenseQuery();

    const { watch, setValue } = useFormContext();

    const productsFieldName = isBookableService
        ? RoutingRulesFormFields.serviceProducts(service.id)
        : RoutingRulesFormFields.additionalServiceProducts(service.id);
    const productsTypeFieldName = `${productsFieldName}.${ProductsFormFields.Type}`;
    const productsType = watch(productsTypeFieldName);
    const selectedProductsFieldName = `${productsFieldName}.${ProductsFormFields.SelectedProducts}`;
    const selectedProductCategoriesFieldName = `${productsFieldName}.${ProductsFormFields.SelectedProductCategories}`;
    const [initialSelectedProducts, setInitialSelectedProducts] = useState<string[]>([]);
    const [initialSelectedProductCategories, setInitialSelectedProductCategories] = useState<string[]>([]);

    const customItemsFieldName = RoutingRulesFormFields.customItems(service.id);
    const { accountingCategoriesOption, accountingCategoriesDialog } = useAccountingCategoriesOption({
        service,
        parentFieldName: productsFieldName,
        reflectedFieldName: customItemsFieldName,
    });

    const selectedProducts = watch(selectedProductsFieldName);
    const selectedProductsCount = selectedProducts?.length ?? 0;
    const selectedProductCategories = watch(selectedProductCategoriesFieldName);
    const selectedProductCategoriesCount = selectedProductCategories?.length ?? 0;

    const localizeProperty = useLocalizeProperty();
    const { deletedProductCategoriesCategory, deletedProductsCategory } = useDeletedItems(service.id);

    const serviceProducts = useMemo(() => {
        if (!products?.products) return [];

        return pipe(
            products.products,
            array.filter((product) => product.serviceId === service.id && !product.isDefault && !product.isDeleted),
            array.sortBy([
                productOrderingContramap,
                pipe(
                    string.Ord,
                    ord.contramap((product: Product) => localizeProperty(product.names))
                ),
            ])
        );
    }, [products?.products, service.id, localizeProperty]);

    const serviceProductCategories = useMemo(
        () => productCategories?.productCategories?.filter((category) => category.serviceId === service.id) ?? [],
        [productCategories?.productCategories, service.id]
    );

    const totalProductsCount = serviceProducts?.length ?? 0;
    const deletedProductCount = deletedProductsCategory?.options?.length ?? 0;
    const totalProductCategoriesCount = serviceProductCategories?.length ?? 0;
    const deletedProductCategoryCount = deletedProductCategoriesCategory?.options?.length ?? 0;

    const localizeNames = (names: { [key: string]: string }) => localizeProperty(names);
    const productOptions = createProductGroupOptions({
        products: serviceProducts,
        productCategories: serviceProductCategories,
        localizeNames,
        deletedProductsCategory,
    });

    const productCategoriesOptions = serviceProductCategories.map((category) => ({
        label: localizeProperty(category.names),
        id: category.id,
        value: category.id,
    }));

    const onProductsDialogOpen = (e: MouseEvent) => {
        e.preventDefault();
        setInitialSelectedProducts(selectedProducts);
        openProductsDialog();
    };

    const onProductCategoriesDialogOpen = (e: MouseEvent) => {
        e.preventDefault();
        setInitialSelectedProductCategories(selectedProductCategories);
        openProductCategoriesDialog();
    };

    useEffect(() => {
        if (productsType == null) {
            setValue(productsTypeFieldName, ProductsOptionsNames.All);
        }
    }, [setValue, productsTypeFieldName, productsType]);

    useEffect(() => {
        if (selectedProducts == null && serviceProducts.length) {
            setValue(
                selectedProductsFieldName,
                serviceProducts.map((product) => product.id)
            );
        }
    }, [selectedProductsFieldName, serviceProducts, setValue, selectedProducts]);

    useEffect(() => {
        if (selectedProductCategories == null && serviceProductCategories.length) {
            setValue(
                selectedProductCategoriesFieldName,
                serviceProductCategories.map((productCategory) => productCategory.id)
            );
        }
    }, [selectedProductCategoriesFieldName, serviceProductCategories, setValue, selectedProductCategories]);

    return (
        <Container padding={['200', '0']}>
            <FormRadioGroupField name={productsTypeFieldName} label={lt(TextKey.RoutingRulesFormProductsLabel)}>
                <RadioButton
                    label={lt(TextKey.RoutingRulesFormAllProductsOption)}
                    value={ProductsOptionsNames.All}
                    key={ProductsOptionsNames.All}
                />
                <RadioButton
                    label={
                        <RadioButtonLabelWithCounter
                            label={lt(TextKey.RoutingRulesFormSelectedProductsOption)}
                            isSelected={productsType === ProductsOptionsNames.SelectedProducts}
                            onDialogOpen={onProductsDialogOpen}
                            selectedItemsCount={selectedProductsCount}
                            totalCount={totalProductsCount}
                            buttonTestId={DataTestId.RoutingRulesProductsEditButton}
                        />
                    }
                    value={ProductsOptionsNames.SelectedProducts}
                    key={ProductsOptionsNames.SelectedProducts}
                    disabled={totalProductsCount === 0 && deletedProductCount === 0}
                />
                <RadioButton
                    label={
                        <RadioButtonLabelWithCounter
                            label={lt(TextKey.RoutingRulesFormSelectedProductCategoriesOption)}
                            isSelected={productsType === ProductsOptionsNames.SelectedProductCategories}
                            onDialogOpen={onProductCategoriesDialogOpen}
                            selectedItemsCount={selectedProductCategoriesCount}
                            totalCount={totalProductCategoriesCount}
                            buttonTestId={DataTestId.RoutingRulesProductCategoriesEditButton}
                        />
                    }
                    value={ProductsOptionsNames.SelectedProductCategories}
                    key={ProductsOptionsNames.SelectedProductCategories}
                    disabled={totalProductCategoriesCount === 0 && deletedProductCategoryCount === 0}
                />
                {newRoutingRulesPOSEnabled && !isBookableService && accountingCategoriesOption}
            </FormRadioGroupField>
            {totalProductsCount || deletedProductCount ? (
                <ProductsDialog
                    {...productsDialogProps}
                    service={service}
                    productOptions={productOptions}
                    selectedProductsCount={selectedProductsCount}
                    totalProductsCount={totalProductsCount}
                    productsFieldName={productsFieldName}
                    initialSelectedProducts={initialSelectedProducts}
                />
            ) : null}
            {totalProductCategoriesCount || deletedProductCategoryCount ? (
                <ProductCategoriesDialog
                    {...productCategoriesDialogProps}
                    service={service}
                    productCategoriesOptions={
                        deletedProductCategoriesCategory
                            ? [...productCategoriesOptions, deletedProductCategoriesCategory]
                            : productCategoriesOptions
                    }
                    selectedProductCategoriesCount={selectedProductCategoriesCount}
                    totalProductCategoriesCount={totalProductCategoriesCount}
                    productsFieldName={productsFieldName}
                    initialSelectedProductCategories={initialSelectedProductCategories}
                />
            ) : null}
            {newRoutingRulesPOSEnabled && !isBookableService && accountingCategoriesDialog}
        </Container>
    );
};
